import { rumInstance, rumEnabled, startRum } from '@/plugins/rum.js';
import { analyticsInstance, analyticsEnabled, startAnalytics } from '@/plugins/analytics.js';

/**
 * Page-level monitoring and analytics.
 *
 * One vocabulary, two destinations: every call here goes to Grafana Faro (for
 * monitoring, beside the backend metrics) and to PostHog (for funnels,
 * retention and paths). A page names the thing that happened once, and both
 * systems describe the same event - so a funnel in one and a panel in the
 * other can never disagree about what was counted.
 *
 * Either sink can be switched off independently, and both are off in
 * development. Every function is a no-op when nothing is configured, and none
 * of them throw: a page must never fail because telemetry did.
 *
 * Pass only values that describe the page, never a person or a record - no
 * names, emails, or record ids. Counts, durations, states and reasons.
 */
export function useRum() {

  const withFaro = (work) => {
    if (!rumEnabled()) {
      return;
    }
    try {
      const faro = rumInstance();
      if (faro) {
        work(faro);
        return;
      }
      // Still downloading: run once it lands so nothing is lost on first paint.
      startRum().then((loaded) => {
        if (loaded) {
          try {
            work(loaded);
          } catch (e) { /* telemetry never breaks the page */ }
        }
      });
    } catch (e) { /* telemetry never breaks the page */ }
  };

  const withPostHog = (work) => {
    if (!analyticsEnabled()) {
      return;
    }
    try {
      const live = analyticsInstance();
      if (live) {
        work(live);
        return;
      }
      startAnalytics().then((loaded) => {
        if (loaded) {
          try {
            work(loaded);
          } catch (e) { /* telemetry never breaks the page */ }
        }
      });
    } catch (e) { /* telemetry never breaks the page */ }
  };

  /**
   * Records that a page was opened. `name` is the page, not the URL - the URL
   * is captured separately and scrubbed.
   */
  const trackPageView = (name, attributes = {}) => {
    const flat = stringify(attributes);
    withFaro((faro) => {
      faro.api.pushEvent('page_view', { page: name, ...flat });
    });
    withPostHog((live) => {
      // PostHog's own $pageview drives its path and funnel tooling, so the
      // page view is sent under that name with the page as a property rather
      // than as a custom event it would not recognise.
      live.capture('$pageview', { page: name, ...flat });
    });
  };

  /**
   * Records something the user did, or something that happened to them.
   *
   * Pass `{ immediate: true }` for an event the user is likely to walk away
   * from. PostHog batches events and sends them every three seconds, or on
   * pagehide, and a phone that switches app fires neither: the tab is frozen
   * with the batch still in it. On 2026-10-05 a sign-up from an iPhone
   * reached the server but its registration_submitted and registration_sent
   * never reached PostHog - the customer went straight to their email to
   * verify. `immediate` sends the event on its own, by beacon where the
   * browser has one. Faro already sends within a quarter of a second, so it
   * is unaffected.
   */
  const trackEvent = (name, attributes = {}, options = {}) => {
    const flat = stringify(attributes);
    withFaro((faro) => {
      faro.api.pushEvent(name, flat);
    });
    withPostHog((live) => {
      if (options && options.immediate) {
        live.capture(name, flat, { send_instantly: true });
      } else {
        live.capture(name, flat);
      }
    });
  };

  /**
   * Records how long something took, in milliseconds. Start it when the work
   * starts and call the returned function when it finishes; calling it twice
   * records nothing the second time.
   *
   *   const done = startTimer('dashboard_load');
   *   ... later ...
   *   done({ tab: 'Overview' });
   *
   * `options` is passed through to trackEvent when the timer finishes.
   */
  const startTimer = (name, options = {}) => {
    const startedAt = Date.now();
    let finished = false;
    return (attributes = {}) => {
      if (finished) {
        return;
      }
      finished = true;
      trackEvent(name, { duration_ms: Date.now() - startedAt, ...attributes }, options);
    };
  };

  /**
   * Reports an error the page handled itself. Unhandled errors are captured
   * automatically by Faro and do not need this.
   */
  const trackError = (error, attributes = {}) => {
    const thrown = error instanceof Error ? error : new Error(String(error));
    const flat = stringify(attributes);
    withFaro((faro) => {
      faro.api.pushError(thrown, { context: flat });
    });
    withPostHog((live) => {
      live.capture('page_error', { message: thrown.message, ...flat });
    });
  };

  /** Faro attribute values must be strings, so both sinks get the same shape. */
  const stringify = (attributes) => {
    const flat = {};
    Object.keys(attributes || {}).forEach((key) => {
      const value = attributes[key];
      if (value === null || value === undefined) {
        return;
      }
      flat[key] = String(value);
    });
    return flat;
  };

  return {
    trackPageView,
    trackEvent,
    startTimer,
    trackError,
    isRumEnabled: rumEnabled(),
    isAnalyticsEnabled: analyticsEnabled(),
  };
}
